"use client";

import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useMe } from "@/hooks/api/useMe";
import { useAuthStore } from "@/stores/authStore";
import { appAbsoluteUrl } from "@/lib/app-base";
import { api } from "@/lib/axios";
import type { User } from "@/lib/types/user";
import {
  Bell,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  LogOut,
  Moon,
  PanelLeft,
  Shield,
  Sun,
  User as UserIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { navGroupsForUser, navItemsForUser, type NavGroup } from "@/components/layout/nav-config";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ShoppingCart } from "lucide-react";
export function AppShell() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, accessToken, logout } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const [collapsed, setCollapsed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [adminOpen, setAdminOpen] = useState(() => pathname.startsWith("/admin"));
  const [marketingOpen, setMarketingOpen] = useState(() => pathname.startsWith("/marketing"));
  const [reportsOpen, setReportsOpen] = useState(() => pathname.startsWith("/reports"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { data: meUser } = useMe();
  const { branchesEnabled } = useBranchesEnabled();
  useBranches(); // keep fetch for internal flows; UI not shown

  const isDark = user?.ui_theme === "dark";
  const themeToggle = useMutation({
    mutationFn: async (next: "light" | "dark") => {
      const { data } = await api.put<{ user: User }>("/api/auth/profile", { ui_theme: next });

      return data.user;
    },
    onMutate: async (next) => {
      if (!user) {
        return;
      }
      setUser({ ...user, ui_theme: next });
    },
    onSuccess: (u) => {
      setUser(u);
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  useEffect(() => {
    if (!accessToken) {
      navigate("/login", { replace: true });
    }
  }, [accessToken, navigate]);

  useEffect(() => {
    if (meUser) {
      setUser(meUser);
    }
  }, [meUser, setUser]);

  // Branch selector removed (no user branch management UI).

  const items = useMemo(
    () => navItemsForUser(user ?? undefined, { branchesEnabled }),
    [user, branchesEnabled],
  );
  const navGroups = useMemo(() => navGroupsForUser(user ?? undefined), [user]);
  const { mainItems, adminItems } = useMemo(() => {
    // Keep nav-config simple (flat list), group admin items here into expandable section.
    const admin = items.filter((i) => i.label.startsWith("Admin · "));
    const main = items.filter((i) => !i.label.startsWith("Admin · "));

    return { mainItems: main, adminItems: admin };
  }, [items]);

  useEffect(() => {
    if (pathname.startsWith("/admin")) {
      setAdminOpen(true);
    }
    if (pathname.startsWith("/marketing")) {
      setMarketingOpen(true);
    }
    if (pathname.startsWith("/reports")) {
      setReportsOpen(true);
    }
  }, [pathname]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  const hasPos = mainItems.some((i) => i.href === "/pos");

  if (!accessToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Checking session…
      </div>
    );
  }

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "RP";

  const sidebarW = collapsed ? "w-sidebar-icon" : "w-sidebar";

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-sm transition-[width] duration-sidebar ease-out md:flex",
          sidebarW,
        )}
      >
        <div
          className={cn(
            "flex w-full shrink-0 items-center justify-center border-b border-sidebar-border",
            collapsed ? "h-14 px-2 py-2" : "min-h-[5.25rem] px-3 py-3",
          )}
        >
          {logoFailed ? (
            <div
              className={cn(
                "flex shrink-0 items-center justify-center rounded-lg bg-sidebar-primary font-semibold text-sidebar-primary-foreground",
                collapsed ? "h-10 w-10 text-sm" : "flex h-16 w-full max-w-full items-center justify-center text-lg",
              )}
            >
              inv
            </div>
          ) : (
            <img
              src="/app-logo.png"
              alt="invoice.cprawat.com"
              className={cn(
                "w-full shrink-0 rounded-lg object-contain object-center",
                collapsed ? "h-10 w-10 max-h-10" : "max-h-16 min-h-[3.25rem] max-w-full",
              )}
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>
        <nav className="flex flex-1 flex-col gap-1 p-2">
          {/* When collapsed, keep original flat icon list (no room for expand UI). */}
          {collapsed ? (
            <>
              {mainItems.map(({ href, label, icon: Icon }) => {
                const active =
                  href === "/dashboard"
                    ? pathname === "/dashboard"
                    : pathname === href || pathname.startsWith(`${href}/`);
                return (
                  <Link
                    key={href}
                    to={href}
                    title={label}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                      "justify-center px-0",
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                  </Link>
                );
              })}
              {navGroups.map((group) => {
                const GroupIcon = group.icon;
                const active = pathname.startsWith(group.basePath);

                return (
                  <DropdownMenu key={group.id}>
                    <DropdownMenuTrigger
                      className={cn(
                        "flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                      )}
                      title={group.label}
                    >
                      <GroupIcon className="h-5 w-5 shrink-0" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" className="w-52">
                      <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {group.children.map(({ href, label, icon: Icon }) => (
                        <DropdownMenuItem
                          key={href}
                          onClick={() => {
                            navigate(href);
                          }}
                        >
                          <Icon className="mr-2 h-4 w-4" />
                          {label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
              {adminItems.map(({ href, label, icon: Icon }) => {
                const active = pathname === href || pathname.startsWith(`${href}/`);
                return (
                  <Link
                    key={href}
                    to={href}
                    title={label}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                      "justify-center px-0",
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                  </Link>
                );
              })}
            </>
          ) : (
            <>
              {mainItems.map(({ href, label, icon: Icon }) => {
                const active =
                  href === "/dashboard"
                    ? pathname === "/dashboard"
                    : pathname === href || pathname.startsWith(`${href}/`);
                return (
                  <Link
                    key={href}
                    to={href}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="truncate">{label}</span>
                  </Link>
                );
              })}

              {navGroups.map((group) => {
                const open =
                  group.id === "marketing"
                    ? marketingOpen
                    : group.id === "reports"
                      ? reportsOpen
                      : false;
                const toggle = () => {
                  if (group.id === "marketing") {
                    setMarketingOpen((o) => !o);
                  } else if (group.id === "reports") {
                    setReportsOpen((o) => !o);
                  }
                };
                const GroupIcon = group.icon;
                const active = pathname.startsWith(group.basePath);

                return (
                  <div key={group.id} className="mt-1">
                    <button
                      type="button"
                      onClick={toggle}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors duration-sidebar",
                        active
                          ? "bg-sidebar-accent/40 text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                      )}
                      aria-expanded={open}
                    >
                      <GroupIcon className="h-5 w-5 shrink-0" />
                      <span className="truncate">{group.label}</span>
                      <ChevronDown
                        className={cn(
                          "ml-auto h-4 w-4 transition-transform duration-sidebar",
                          open ? "rotate-0" : "-rotate-90",
                        )}
                      />
                    </button>

                    {open ? (
                      <div className="mt-1 space-y-1 pl-3">
                        {group.children.map(({ href, label, icon: Icon }) => {
                          const childActive = pathname === href || pathname.startsWith(`${href}/`);

                          return (
                            <Link
                              key={href}
                              to={href}
                              className={cn(
                                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                                childActive
                                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                  : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                              )}
                            >
                              <Icon className="h-5 w-5 shrink-0 opacity-90" />
                              <span className="truncate">{label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {adminItems.length > 0 ? (
                <div className="mt-1">
                  <button
                    type="button"
                    onClick={() => setAdminOpen((o) => !o)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors duration-sidebar",
                      pathname.startsWith("/admin")
                        ? "bg-sidebar-accent/40 text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                    )}
                    aria-expanded={adminOpen}
                  >
                    <Shield className="h-5 w-5 shrink-0" />
                    <span className="truncate">Admin</span>
                    <ChevronDown
                      className={cn(
                        "ml-auto h-4 w-4 transition-transform duration-sidebar",
                        adminOpen ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </button>

                  {adminOpen ? (
                    <div className="mt-1 space-y-1 pl-3">
                      {adminItems.map(({ href, label, icon: Icon }) => {
                        const active = pathname === href || pathname.startsWith(`${href}/`);
                        const short = label.replace(/^Admin\s+·\s+/, "");

                        return (
                          <Link
                            key={href}
                            to={href}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-sidebar",
                              active
                                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
                            )}
                          >
                            <Icon className="h-5 w-5 shrink-0 opacity-90" />
                            <span className="truncate">{short}</span>
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </nav>
        <div className="border-t border-sidebar-border p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full justify-center text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      <div
        className={cn(
          "flex min-h-screen flex-1 flex-col transition-[margin] duration-sidebar ease-out",
          "ml-0",
          collapsed ? "md:ml-[var(--sidebar-width-icon)]" : "md:ml-[var(--sidebar-width)]",
        )}
      >
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-card/90 px-3 pt-safe backdrop-blur-md supports-[backdrop-filter]:bg-card/75 md:gap-4 md:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Open menu"
              className="md:hidden"
              onClick={() => setMobileNavOpen(true)}
            >
              <PanelLeft className="h-5 w-5" />
            </Button>
            {hasPos ? (
              <Link
                to="/pos"
                className={cn(buttonVariants({ variant: "outline", size: "sm" }), "md:hidden")}
              >
                <ShoppingCart className="mr-1.5 h-4 w-4" />
                POS
              </Link>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground"
              aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
              disabled={themeToggle.isPending || !user}
              onClick={() => themeToggle.mutate(isDark ? "light" : "dark")}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" className="text-muted-foreground" aria-label="Notifications">
              <Bell className="h-5 w-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  buttonVariants({ variant: "ghost", size: "icon" }),
                  "h-9 w-9 rounded-full p-0",
                )}
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-0.5">
                    <span className="text-sm font-medium">{user?.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/profile");
                  }}
                >
                  <UserIcon className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={async () => {
                    qc.clear();
                    await logout();
                    window.location.href = appAbsoluteUrl("login");
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 pb-safe md:p-6">
          <Outlet />
        </main>
      </div>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[min(100vw-2rem,18rem)] border-sidebar-border bg-sidebar p-0 text-sidebar-foreground">
          <SheetHeader className="border-b border-sidebar-border">
            <SheetTitle className="text-sidebar-foreground">Menu</SheetTitle>
          </SheetHeader>
          <nav className="flex max-h-[calc(100dvh-4rem)] flex-col gap-1 overflow-y-auto p-2">
            {mainItems.map(({ href, label, icon: Icon }) => {
              const active =
                href === "/dashboard"
                  ? pathname === "/dashboard"
                  : pathname === href || pathname.startsWith(`${href}/`);
              return (
                <Link
                  key={href}
                  to={href}
                  onClick={() => setMobileNavOpen(false)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium",
                    active
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60",
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="truncate">{label}</span>
                </Link>
              );
            })}
            {navGroups.map((group) => {
              const open =
                group.id === "marketing" ? marketingOpen : group.id === "reports" ? reportsOpen : false;
              const toggle = () => {
                if (group.id === "marketing") setMarketingOpen((o) => !o);
                else if (group.id === "reports") setReportsOpen((o) => !o);
              };
              const GroupIcon = group.icon;
              return (
                <div key={group.id} className="mt-1">
                  <button
                    type="button"
                    onClick={toggle}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-sidebar-foreground/80"
                  >
                    <GroupIcon className="h-5 w-5 shrink-0" />
                    <span className="truncate">{group.label}</span>
                    <ChevronDown className={cn("ml-auto h-4 w-4", open ? "" : "-rotate-90")} />
                  </button>
                  {open
                    ? group.children.map(({ href, label, icon: Icon }) => (
                        <Link
                          key={href}
                          to={href}
                          onClick={() => setMobileNavOpen(false)}
                          className="flex items-center gap-3 rounded-lg py-2 pl-9 pr-3 text-sm"
                        >
                          <Icon className="h-4 w-4 shrink-0" />
                          {label}
                        </Link>
                      ))
                    : null}
                </div>
              );
            })}
            {adminItems.length > 0 ? (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={() => setAdminOpen((o) => !o)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium"
                >
                  <Shield className="h-5 w-5 shrink-0" />
                  Admin
                  <ChevronDown className={cn("ml-auto h-4 w-4", adminOpen ? "" : "-rotate-90")} />
                </button>
                {adminOpen
                  ? adminItems.map(({ href, label, icon: Icon }) => (
                      <Link
                        key={href}
                        to={href}
                        onClick={() => setMobileNavOpen(false)}
                        className="flex items-center gap-3 rounded-lg py-2 pl-9 pr-3 text-sm"
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        {label.replace(/^Admin\s+·\s+/, "")}
                      </Link>
                    ))
                  : null}
              </div>
            ) : null}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
